import React, { useState, useEffect } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import "./ProfessorProfile.css";
import {
	Search,
	Menu,
	X,
	BookCheck,
	BookMarked,
	FolderGit2,
	SquareActivity,
	Atom,
	Newspaper,
	FolderSearch,
	Award,
	SquareUserRound,
	Cog,
	FolderOpenDot,
	Inbox,
} from "lucide-react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

const API_URL = process.env.REACT_APP_API_URL;

function Tabs({ defaultValue, children }) {
	const location = useLocation();
	const navigate = useNavigate();
	const getInitialTab = () => {
		const hash = location.hash.replace("#", "");
		return hash || defaultValue;
	};

	const [activeTab, setActiveTab] = useState(getInitialTab());

	const handleTabChange = (tab) => {
		setActiveTab(tab);
		navigate(`#${tab}`, { replace: true });
	};

	return (
		<div className="tabs">
			{React.Children.map(children, (child) => {
				return React.cloneElement(child, {
					activeTab,
					setActiveTab: handleTabChange,
				});
			})}
		</div>
	);
}

function TabsList({ children, activeTab, setActiveTab }) {
	return (
		<div className="tabs-list">
			{React.Children.map(children, (child) => {
				return React.cloneElement(child, { activeTab, setActiveTab });
			})}
		</div>
	);
}

function TabsTrigger({ value, children, activeTab, setActiveTab }) {
	return (
		<button
			className={`tab-trigger ${
				activeTab === value ? "active" : ""
			} medium-text`}
			onClick={() => setActiveTab(value)}
		>
			{children}
		</button>
	);
}

function TabsContent({ value, children, activeTab }) {
	return activeTab === value ? (
		<div className={`tab-content ${value === "bio" ? "bio" : ""}`}>
			{children}
		</div>
	) : null;
}

const getIconForType = (typeId) => {
	const icons = {
		1: <BookCheck />,
		2: <BookMarked />,
		3: <FolderGit2 />,
		4: <SquareActivity />,
		5: <Atom />,
		6: <Newspaper />,
		7: <FolderSearch />,
		8: <Award />,
		9: <SquareUserRound />,
		10: <Cog />,
		11: <FolderOpenDot />,
	};
	return icons[typeId] || "";
};

function FacultyProfile() {
	const { id } = useParams();
	const [faculty, setFaculty] = useState(null);
	const [activities, setActivities] = useState(null);
	const [loading, setLoading] = useState({
		faculty: true,
		activities: false,
	});
	const [error, setError] = useState(null);
	const [selectedActivityType, setSelectedActivityType] = useState(null);
	const [sidebarOpen, setSidebarOpen] = useState(() => {
		return window.innerWidth >= 768;
	});
	const [searchQuery, setSearchQuery] = useState("");

	const lastDegree = [
        'کارشناسی',
        'کارشناسی ارشد',
        'دکترای تخصصی',
    ];

	const typeOfResume = {
		1: "دروس ارائه شده",
		2: "كتب تاليفی، ترجمه، ويرایش، چاپ مجدد",
		3: "پروژه‌های كارشناسی",
		4: "پایان‌نامه‌های تحصیلات تكمیلی",
		5: "مقالات ملی و بین المللی",
		6: "مقالات ارائه‌شده در همایش‌ها",
		7: "طرح‌های پژوهشی",
		8: "جوایز و افتخارات",
		9: "عضویت در مجامع ملی و بین‌المللی",
		10: "اختراعات",
		11: "فعاليت‌های اجرایی",
	};

	const fields = {
		1: { section: { label: "مقطع", name: "section", value: "" } },

		2: {
			year: { label: "سال", name: "year", value: "" },
			ISBN: { label: "ISBN", name: "ISBN", value: "" },
			link: {
				label: "لینک کتاب",
				value: "link",
				value: "",
				optional: true,
			},
		},

		3: {
			student_name: {
				label: "نام دانشجو",
				name: "student_name",
				value: "",
			},
			date: { label: "تاریخ", name: "date", value: "", optional: true },
		},

		4: {
			student_name: {
				label: "نام دانشجو",
				name: "student_name",
				value: "",
			},
			date: { label: "تاریخ", name: "date", value: "", optional: true },
		},

		5: {
			venue: { label: "عنوان مجله", name: "venue", value: "" },
			year: { label: "سال", name: "year", value: "" },
		},

		6: {
			venue: { label: "محل برگزاری", name: "venue", value: "" },
			date: { label: "تاریخ", name: "date", value: "", optional: true },
			authors: { label: "نویسنده", name: "authors", value: "" },
			conference_title: {
				label: "عنوان کنفرانس",
				name: "conference_title",
				value: "",
			},
		},

		7: {
			year: { label: "سال", name: "year", value: "" },
			teammate: { label: "همکار", name: "teammate", value: "" },
		},

		8: {
			venue: { label: "محل برگزاری", name: "venue", value: "" },
			date: { label: "تاریخ", name: "date", value: "", optional: true },
		},

		9: {
			start_year: { label: "سال شروع", name: "start_year", value: "" },
			end_year: { label: "سال پایان", name: "end_year", value: "" },
		},

		10: {
			venue: { label: "محل برگزاری", name: "venue", value: "" },
			year: { label: "سال", name: "year", value: "" },
			teammate: { label: "همکار", name: "teammate", value: "" },
			subject: { label: "موضوع", name: "subject", value: "" },
		},

		11: {
			start_year: { label: "سال شروع", name: "start_year", value: "" },
			end_year: {
				label: "سال پایان",
				name: "end_year",
				value: "",
				optional: true,
			},
		},
	};

	const arabicToPersianMap = {
		ك: "ک",
		ي: "ی",
		ة: "ه",
		أ: "ا",
		إ: "ا",
		آ: "آ",
		ؤ: "و",
		ئ: "ی",
		ٱ: "ا",
		"ٰ": "‌",
		"": "",
	};

	const convertArabicToPersian = (text) => {
		if (!text) return text;

		return text
			.split("")
			.map((char) => arabicToPersianMap[char] || char)
			.join("")
			.replace(/\u0643/g, "\u06A9")
			.replace(/\u064A/g, "\u06CC")
			.replace(/\u0626/g, "\u06CC")
			.replace(/\u0624/g, "\u0648")
			.replace(/\u0625/g, "\u0627")
			.replace(/\u0622/g, "\u0622")
			.replace(/\u0623/g, "\u0627");
	};

	const filteredActivities = activities?.filter(
		(activity) =>
			activity.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
			activity.description
				?.toLowerCase()
				.includes(searchQuery.toLowerCase()),
	);

	const fetchActivityData = async (type_id) => {
		setLoading((prev) => ({ ...prev, activities: true }));
		setSelectedActivityType(type_id);

		try {
			const response = await fetch(
				`${API_URL}/public/activity/${Number(id)}/${type_id}`,
			);
			if (!response.ok) {
				throw new Error("Network response was not ok");
			}
			const data = await response.json();

			const convertedActivities = data.map((activity) => {
				const convertedActivity = {
					...activity,
					title: convertArabicToPersian(activity.title),
					description: convertArabicToPersian(activity.description),
				};

				if (activity.sub_activity) {
					const convertedSubActivity = {};
					for (const key in activity.sub_activity) {
						const value = activity.sub_activity[key];
						convertedSubActivity[key] =
							typeof value === "string"
								? convertArabicToPersian(value)
								: value;
					}
					convertedActivity.sub_activity = convertedSubActivity;
				}
				return convertedActivity;
			});

			setActivities(convertedActivities);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading((prev) => ({ ...prev, activities: false }));
		}
	};

	useEffect(() => {
		fetchActivityData(1);
	}, []);

	useEffect(() => {
		const fetchFacultyData = async () => {
			try {
				const response = await fetch(
					`${API_URL}/public/profile/${Number(id)}`,
				);
				if (!response.ok) {
					throw new Error("Network response was not ok");
				}
				const data = await response.json();
				const convertedData = {
					...data,
					persian_fname: convertArabicToPersian(data.persian_fname),
					persian_lname: convertArabicToPersian(data.persian_lname),
					scientific_rank: convertArabicToPersian(
						data.scientific_rank,
					),
					department: convertArabicToPersian(data.department),
				};

				setFaculty(convertedData);
			} catch (err) {
				setError(err.message);
			} finally {
				setLoading((prev) => ({ ...prev, faculty: false }));
			}
		};

		fetchFacultyData();
	}, [id]);

	const isRTL = (text) => {
		const rtlChars = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
		return rtlChars.test(text);
	};

	if (error) {
		return (
			<div className="page-container">
				<main className="content">
					<h1 className="error-message">
						خطا در دریافت اطلاعات: {error}
					</h1>
					<Link to="/home" className="back-button-home">
						بازگشت به فهرست اساتید
					</Link>
				</main>
				<Footer />
			</div>
		);
	}

	if (!faculty) {
		return <></>;
	}

	return (
		<div className="page-container">
			<Header />
			<main className="faculty-profile">
				<div className="profile-header">
					<div className="header-content">
						<Link to="/home" className="back-button-home">
							<span>بازگشت</span>
						</Link>
						<div className="faculty-info">
							<h2>
								رزومه علمی{" "}
								<span>
									{faculty.persian_fname +
										" " +
										faculty.persian_lname}
								</span>
							</h2>
						</div>
					</div>
				</div>
				<div className="tabs-container">
					<Tabs defaultValue="bio">
						<TabsList>
							<TabsTrigger value="bio">مشخصات فردی</TabsTrigger>
							<TabsTrigger
								value="education"
								onClick={() => setSelectedActivityType(null)}
							>
								فعالیت ها
							</TabsTrigger>
						</TabsList>
						<TabsContent value="bio">
							<div>
								<h3>مشخصات فردی</h3>

								{faculty.persian_fname && (
									<p>
										<span>نام:</span>{" "}
										{faculty.persian_fname}
									</p>
								)}

								{faculty.persian_lname && (
									<p>
										<span>نام خانوادگی:</span>{" "}
										{faculty.persian_lname}
									</p>
								)}

								{faculty.organizational_post && (
									<p>
										<span>پست سازمانی:</span>{" "}
										{faculty.organizational_post}
									</p>
								)}
							</div>

							<div>
								<h3>مشخصات تماس</h3>

								{faculty.email && (
									<p>
										<span>پست الکترونیک دانشگاهی:</span>{" "}
										{faculty.email}
									</p>
								)}

								{faculty.second_email && (
									<p>
										<span>پست الکترونیک شخصی:</span>{" "}
										{faculty.second_email}
									</p>
								)}

								{faculty.personal_website1 && (
									<p>
										<span>وبسایت شخصی:</span>{" "}
										<a
											href={faculty.personal_website1}
											target="_blank"
											rel="noopener noreferrer"
										>
											{faculty.personal_website1}
										</a>
									</p>
								)}
							</div>

							<div>
								<h3>وضعیت تحصیلی</h3>

								{faculty.scientific_rank && (
									<p>
										<span>مرتبه علمی:</span>{" "}
										{faculty.scientific_rank}
									</p>
								)}

								{faculty.department && (
									<p>
										<span>گرایش:</span> {faculty.department}
									</p>
								)}

								{faculty.last_degree && (
									<p>
										<span>آخرین مدرک تحصیلی:</span>{" "}
										{lastDegree[faculty.last_degree - 1]}
									</p>
								)}
							</div>
						</TabsContent>
						<TabsContent value="education" className="education">
							<div className="activity-container">
								<button
									className="sidebar-toggle"
									onClick={() => setSidebarOpen(!sidebarOpen)}
								>
									{sidebarOpen ? <X size={20} /> : <Menu />}
									<span>انواع فعاليت‌ها</span>
								</button>
								<div
									className={`activity-sidebar ${
										sidebarOpen ? "open" : ""
									}`}
								>
									<div className="activity-types">
										<div className="activity-buttons">
											{Object.entries(typeOfResume).map(
												([type_id, title]) => (
													<div
														key={type_id}
														className={`activity-button-item ${
															selectedActivityType ===
															Number(type_id)
																? "active"
																: ""
														}`}
														onClick={() => {
															fetchActivityData(
																Number(type_id),
															);
															if (
																window.innerWidth <
																768
															)
																setSidebarOpen(
																	false,
																);
														}}
													>
														<div className="activity-button-icon">
															{getIconForType(
																Number(type_id),
															)}
														</div>
														<span className="activity-button-title">
															{title}
														</span>
														<div className="activity-button-indicator"></div>
													</div>
												),
											)}
										</div>
									</div>
								</div>
								<div className="activity-main-content">
									{selectedActivityType && (
										<div className="activity-results">
											<div className="activity-header">
												<div className="search-container-profile">
													<div className="input-wrapper">
														<Search className="search-icon" />
														<input
															type="search"
															className="search-input medium-text"
															placeholder="جستجو در فعالیت‌ها..."
															value={searchQuery}
															onChange={(e) =>
																setSearchQuery(
																	e.target
																		.value,
																)
															}
															style={{
																fontFamily:
																	"Vazir, sans-serif",
															}}
														/>
													</div>
												</div>
												<h3>
													{
														typeOfResume[
															selectedActivityType
														]
													}
												</h3>
											</div>
											{error ? (
												<div className="status-message error-message">
													خطا در دریافت فعالیت‌ها:{" "}
													{error}
												</div>
											) : activities ? (
												<div className="activities-grid">
													{filteredActivities.length >
													0 ? (
														filteredActivities.map(
															(
																activity,
																index,
															) => {
																const activityData =
																	{
																		...activity,
																		...(activity.sub_activity ||
																			{}),
																	};
																return (
																	<div
																		key={
																			index
																		}
																		className="activity-card"
																	>
																		<div
																			className="activity-title"
																			dir={
																				isRTL(
																					activity.title,
																				)
																					? "rtl"
																					: "ltr"
																			}
																		>
																			{
																				activity.title
																			}
																		</div>
																		{activity.description && (
																			<div className="activity-description">
																				{
																					activity.description
																				}
																			</div>
																		)}
																		<div className="activity-fields">
																			{fields[
																				selectedActivityType
																			] &&
																				Object.entries(
																					fields[
																						selectedActivityType
																					],
																				).map(
																					([
																						fieldKey,
																						fieldData,
																					]) => {
																						if (
																							!activityData[
																								fieldKey
																							] ||
																							activityData[
																								fieldKey
																							] ===
																								""
																						)
																							return null;
																						return (
																							<div
																								key={
																									fieldKey
																								}
																								className="activity-field"
																							>
																								<span className="field-label">
																									{
																										fieldData.label
																									}

																									:
																								</span>
																								<span className="field-value">
																									{fieldKey ===
																									"link" ? (
																										<a
																											href={
																												activityData[
																													fieldKey
																												]
																											}
																											target="_blank"
																											rel="noopener noreferrer"
																										>
																											مشاهده
																											لینک
																										</a>
																									) : (
																										activityData[
																											fieldKey
																										]
																									)}
																								</span>
																							</div>
																						);
																					},
																				)}
																		</div>
																	</div>
																);
															},
														)
													) : (
														<div className="empty-state">
															<Inbox size={30} />
															<p>
																{searchQuery
																	? "نتیجه‌ای برای جستجوی شما یافت نشد"
																	: "هیچ فعالیتی یافت نشد"}
															</p>
														</div>
													)}
												</div>
											) : (
												<div className="status-message">
													برای مشاهده فعالیت‌ها، نوع
													فعالیت را انتخاب کنید
												</div>
											)}
										</div>
									)}
								</div>
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</main>
			<Footer />
		</div>
	);
}

export default FacultyProfile;
